// Morning Landing Page Configuration
// All event details are configurable here without a code deployment.

export const MORNING_CONFIG = {
  phone: '(713) 570-6716',
  whatsappUrl: 'https://wa.me/17135706716',
  address: '1729 Brittmoore Rd Ste A, Houston, TX 77043',
  googleMapsUrl:
    'https://www.google.com/maps/dir/?api=1&destination=1729+Brittmoore+Rd+Ste+A,+Houston,+TX+77043',
  morningHours: 'Mon\u2013Fri 7:00 AM \u2013 10:00 AM',
  facilities: {
    padelCourts: 5,
    pickleballCourts: 4,
  },
} as const;

// ---------------------------------------------------------------------------
// Event types
// ---------------------------------------------------------------------------

export type EventType = 'free_intro' | 'paid_clinic' | 'social_play';
export type EventStatus = 'available' | 'few_spots' | 'sold_out' | 'waitlist';

export interface MorningEvent {
  id: string;
  eventType: EventType;
  day: string;
  time: string; // display range, e.g. "9:00\u201310:00 AM"
  startHour: number;
  startMin: number;
  endHour: number;
  endMin: number;
  name: string;
  eyebrow: string;
  headline: string;
  body: string;
  ctaText: string;
  submitText: string;
  microcopy: string;
  status: EventStatus;
  spotsLeft?: number;
  price?: string;
}

// ---------------------------------------------------------------------------
// Weekly schedule \u2013 update status / spotsLeft here to toggle scarcity states
// ---------------------------------------------------------------------------

export const MAX_CAPACITY: Record<string, number> = {
  'wed-9am': 6,
  'wed-6pm': 6,
  'thu-6pm': 6,
  'sat-9am': 6,
  'fri-social': 8,
};

export const FEW_SPOTS_THRESHOLD = 2;

// ---------------------------------------------------------------------------
// Weekly schedule – update status / spotsLeft here to toggle scarcity states
// ---------------------------------------------------------------------------

export const MORNING_EVENTS: MorningEvent[] = [
  {
    id: 'wed-9am',
    eventType: 'free_intro',
    day: 'Wednesday',
    time: '9:00\u20139:45 AM',
    startHour: 9,
    startMin: 0,
    endHour: 9,
    endMin: 45,
    name: 'Free Intro to Padel',
    eyebrow: 'FREE \u00B7 BEGINNER FRIENDLY',
    headline: 'Try Padel for Free at 9:00 AM',
    body: 'A coach-led first session for people who have never played or barely know what padel is.',
    ctaText: 'Reserve Free Spot',
    submitText: 'Reserve My Free Spot',
    microcopy: 'Free. Coach-led. Beginner-friendly. Perfect if you have never played.',
    status: 'available',
  },
  {
    id: 'wed-6pm',
    eventType: 'free_intro',
    day: 'Wednesday',
    time: '6:00\u20137:00 PM',
    startHour: 18,
    startMin: 0,
    endHour: 19,
    endMin: 0,
    name: 'Free Intro to Padel',
    eyebrow: 'FREE \u00B7 BEGINNER FRIENDLY',
    headline: 'Try Padel for Free at 6:00 PM',
    body: 'A coach-led first session for people who have never played or barely know what padel is.',
    ctaText: 'Reserve Free Spot',
    submitText: 'Reserve My Free Spot',
    microcopy: 'Free. Coach-led. Beginner-friendly. Perfect if you have never played.',
    status: 'available',
  },
  {
    id: 'thu-6pm',
    eventType: 'free_intro',
    day: 'Thursday',
    time: '6:00\u20137:00 PM',
    startHour: 18,
    startMin: 0,
    endHour: 19,
    endMin: 0,
    name: 'Free Intro to Padel',
    eyebrow: 'FREE \u00B7 BEGINNER FRIENDLY',
    headline: 'Try Padel for Free at 6:00 PM',
    body: 'A coach-led first session for people who have never played or barely know what padel is.',
    ctaText: 'Reserve Free Spot',
    submitText: 'Reserve My Free Spot',
    microcopy: 'Free. Coach-led. Beginner-friendly. Perfect if you have never played.',
    status: 'available',
  },
  {
    id: 'sat-9am',
    eventType: 'free_intro',
    day: 'Saturday',
    time: '9:00\u201310:00 AM',
    startHour: 9,
    startMin: 0,
    endHour: 10,
    endMin: 0,
    name: 'Free Intro to Padel',
    eyebrow: 'FREE \u00B7 BEGINNER FRIENDLY',
    headline: 'Try Padel for Free at 9:00 AM',
    body: 'A coach-led first session for people who have never played or barely know what padel is.',
    ctaText: 'Reserve Free Spot',
    submitText: 'Reserve My Free Spot',
    microcopy: 'Free. Coach-led. Beginner-friendly. Perfect if you have never played.',
    status: 'available',
  },
  {
    id: 'fri-social',
    eventType: 'social_play',
    day: 'Friday',
    time: '9:00\u201311:00 AM',
    startHour: 9,
    startMin: 0,
    endHour: 11,
    endMin: 0,
    name: 'Morning Open Play',
    eyebrow: 'FRIDAY \u00B7 HOSTED',
    headline: 'Start Friday With Open Play',
    body: 'Meet players, get moving, and make mornings more social without giving up your evening.',
    ctaText: 'Save My Spot',
    submitText: 'Save My Friday Spot',
    microcopy: 'An easy way to play, meet people, and keep mornings active.',
    status: 'available',
  },
];

// ---------------------------------------------------------------------------
// Trust strip
// ---------------------------------------------------------------------------

export const TRUST_POINTS = [
  'Premium Houston Club',
  '5 Outdoor Padel Courts',
  'Showers & Changing Rooms',
  'Sauna + Cold Plunge',
  'Coach-Led Sessions',
] as const;

// ---------------------------------------------------------------------------
// FAQ
// ---------------------------------------------------------------------------

export const MORNING_FAQ = [
  {
    question: 'I have never played padel. Can I still join?',
    answer:
      'Our Wednesday, Thursday, and Saturday free intro sessions are designed for complete beginners. A coach will walk you through everything \u2014 no experience needed.',
  },
  {
    question: 'Do I need my own racket or gear?',
    answer:
      'Nope. We provide rackets and balls for all sessions. Just come in comfortable athletic clothing and court shoes.',
  },
  {
    question: 'Which session is best if I am brand new?',
    answer:
      "Start with a Free Intro on Wednesday at 9:00 AM or 6:00 PM, Thursday at 6:00 PM, or Saturday at 9:00 AM \u2014 they're coach-led, beginner-only, and completely free. From there, many players move on to Friday Morning Open Play.",
  },
  {
    question: 'Can I come alone?',
    answer:
      "Yes! Most people come solo. All our morning sessions are organized \u2014 we handle partner matching and group setup so you'll meet people right away.",
  },
  {
    question: 'What should I wear?',
    answer:
      'Comfortable athletic clothing and court shoes (tennis shoes work great). We provide everything else. TEMPO also has showers and changing rooms so you can get ready for the rest of your day.',
  },
  {
    question: 'How early should I arrive?',
    answer:
      'Please arrive 15 minutes before your session starts. This gives you time to check in, meet your group, and get set up.',
  },
  {
    question: 'Where is TEMPO located?',
    answer:
      '1729 Brittmoore Rd Ste A, Houston, TX 77043 \u2014 in the Energy Corridor / Spring Branch area. Easy access with plenty of free parking.',
  },
  {
    question: 'What happens after I register?',
    answer:
      "We'll send you an email to confirm your spot with session details, directions, and what to expect. If you haven't received an email, call us at (713) 570-6716.",
  },
] as const;

// ---------------------------------------------------------------------------
// Testimonials
// ---------------------------------------------------------------------------

export const MORNING_TESTIMONIALS = [
  {
    quote:
      'I was nervous to try something new, but the coach made it so easy. Best morning decision I\u2019ve made!',
    author: 'Sarah M.',
    rating: 5,
  },
  {
    quote:
      'Perfect after school drop-off. I finally have something fun just for me in the mornings.',
    author: 'Jessica L.',
    rating: 5,
  },
  {
    quote:
      "Never heard of padel before. Now it's my favorite part of the week.",
    author: 'Amanda R.',
    rating: 5,
  },
  {
    quote: 'Came alone and left with new friends and a new weekly habit.',
    author: 'Michelle K.',
    rating: 5,
  },
  {
    quote: 'Play, shower, and on with my day. The facilities are top-notch.',
    author: 'Laura G.',
    rating: 5,
  },
] as const;
